import { lazy, useState, createElement } from 'react';

// A lazy route whose code can be asked for before the route is shown.
//
// React.lazy starts the download only when the page is first drawn, and a
// page that was not ready when it was first drawn sits behind its Suspense
// placeholder for at least ~300 ms, however fast the download was. With
// `Page.preload()` called ahead of time (while a sign-in check or a sign-in
// is still under way) the code is usually there by the time the page is
// needed, and it is then drawn straight away with no placeholder at all.
// When it is not there yet, this behaves exactly like React.lazy.
export function lazyWithPreload(factory) {
  let promise = null;
  let Loaded = null;

  const load = () => {
    if (!promise) {
      promise = factory().then(
        (mod) => { Loaded = mod.default; return mod; },
        // A failed download can be asked for again.
        (err) => { promise = null; throw err; },
      );
    }
    return promise;
  };

  const Lazy = lazy(load);

  function Preloadable(props) {
    // Decided once per mount. Changing from the lazy component to the loaded
    // one on a later render would be a different component to React: the page
    // would be thrown away and mounted again, losing its state.
    const [Component] = useState(() => Loaded || Lazy);
    return createElement(Component, props);
  }

  Preloadable.preload = () => { load().catch(() => {}); };
  return Preloadable;
}
